import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import ApiClient from '../../utils/apiClient';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [passwordFormData, setPasswordFormData] = React.useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [changingPassword, setChangingPassword] = React.useState(false);

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    setChangingPassword(true);
    setPasswordError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPasswordError('Authentication token not found');
        setChangingPassword(false);
        return;
      }

      const data = await ApiClient.put('api/users/password', {
        current_password: passwordFormData.current_password,
        new_password: passwordFormData.new_password,
        confirm_password: passwordFormData.confirm_password
      });

      if (data.success) {
        // Close dialog on success
        handleClose();
      } else {
        setPasswordError(data.msg || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Network error occurred while changing password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleClose = () => {
    setPasswordFormData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setPasswordError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="password-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="password-dialog-title">
        Change Password
      </DialogTitle>
      <DialogContent>
        {passwordError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {passwordError}
          </Alert>
        )}
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: '8px 16px 8px 0', width: isMobile ? '155px' : '200px' }}>
                  <Typography variant="body1">Current Password:</Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', padding: '8px 0' }}>
                  <TextField
                    type="password"
                    value={passwordFormData.current_password}
                    onChange={(e) => handlePasswordInputChange('current_password', e.target.value)}
                    fullWidth
                    required
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: '8px 16px 8px 0', width: isMobile ? '155px' : '200px' }}>
                  <Typography variant="body1">New Password:</Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', padding: '8px 0' }}>
                  <TextField
                    type="password"
                    value={passwordFormData.new_password}
                    onChange={(e) => handlePasswordInputChange('new_password', e.target.value)}
                    fullWidth
                    required
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: '8px 16px 8px 0', width: isMobile ? '155px' : '200px' }}>
                  <Typography variant="body1">Confirm Password:</Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', padding: '8px 0' }}>
                  <TextField
                    type="password"
                    value={passwordFormData.confirm_password}
                    onChange={(e) => handlePasswordInputChange('confirm_password', e.target.value)}
                    fullWidth
                    required
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={changingPassword}
          sx={{ 
            color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary',
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleChangePassword}
          variant="contained"
          disabled={changingPassword}
        >
          {changingPassword ? 'Changing...' : 'Change Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
